import merge from 'lodash/merge';

/**
 * fetchHomeContentModule - will get the employer specific content module
 * or the default if no employer specific file exists for student home.
 *
 * ```tsx
 * const contentModule = await fetchHomeContentModule(employerId ?? 'default', moduleName, { ...variables });
 *
 * setContent({ // use reducer set state fn
 *   type: 'success',
 *   payload: { FormData: contentModule.module.data },
 * });
 * ```
 *
 * NOTE: The content files being imported here must include an 'export default [x]' statement,
 * or the import will fail silently.
 *
 * @packageDocumentation
 */

export const fetchHomeContentModule = async <T>(
  employerId: string,
  moduleName: string,
  variables: Record<string, unknown> = {}
) => {
  type ContentModule = { Error: string } & {
    default?: (variables?: Record<string, unknown>) => T;
  };
  try {
    const imports: Array<Promise<ContentModule>> = [
      import(
        /* webpackChunkName: "content" */
        `../__content__/${moduleName}/default.ts`
      ),
      import(
        /* webpackChunkName: "content" */
        `../__content__/${moduleName}/${employerId}.ts`
        // eslint-disable-next-line @typescript-eslint/no-empty-function
      ).catch(() => {}),
    ];
    const results = await Promise.all(imports);
    const [defaultContent, employerContent] = results.map((file) =>
      file?.default?.call(null, variables)
    );
    const response = merge(defaultContent!, employerContent);
    return {
      [moduleName]: response,
    };
  } catch (error) {
    return {
      [moduleName]: { error },
    };
  }
};

/**
 * contentReducer - reducer used in useReducer in tandem with fetchHomeContentModule
 * to set the page content.
 *
 * ```tsx
 * const [{ loading, error, formData }, setContent] = useReducer(
 *  contentReducer,
 *   {
 *     loading: true,
 *     error: false,
 *   }
 * );
 * ```
 *
 * @packageDocumentation
 */

export const contentReducer = (state: any, action: any) => {
  switch (action.type) {
    case 'success': {
      return {
        loading: false,
        error: false,
        ...action.payload,
      };
    }
    case 'failure': {
      return {
        ...state,
        loading: false,
        error: true,
      };
    }

    default:
      return state;
  }
};
